import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';

// Components
import CircleCheckIconNucleo from '@icons/CircleCheckIconNucleo';
import PhotoUpload from '@molecules/PhotoUpload';
import { BodySmall, H4, LabelLarge, LinkSmall } from '@styled';

// Helpers
import colors from '@general/colors';
import { ADD_PROPERTY_SITE_PLAN, REMOVE_PROPERTY_SITE_PLAN } from '@general/graphql/properties.graph';

const FilePreviewContainerOuter = styled.div`
    aspect-ratio: 16/9;
    border: 2px solid ${colors.lightGray};
    max-width: 356px;
    position: relative;
`;

const FilePreviewContainerInner = styled.div`
    align-items: center;
    background-size: cover;
    display: flex;
    height: 100%;
    justify-content: center;
    position: relative;
    width: 100%;
`;

const FilePreviewBanner = styled.div`
    background-color: ${colors.lightGray};
    bottom: 0;
    left: 0;
    overflow: hidden;
    padding: 9px 12px;
    position: absolute;
    width: 100%;
`;

const PropertySitePlan = ({ propertyId, sitePlan: sitePlanInitial }) => {
    const [sitePlan, setSitePlan] = useState(sitePlanInitial);
    const [addSitePlanMutation] = useMutation(ADD_PROPERTY_SITE_PLAN);
    const [removeSitePlanMutation] = useMutation(REMOVE_PROPERTY_SITE_PLAN);

    const addSitePlan = files => {
        files.map(async file => {
            const {
                data: {
                    addPropertySitePlan: { id, recordId, uploadUrl, url },
                },
            } = await addSitePlanMutation({
                variables: { propertyId },
            });

            await fetch(uploadUrl, { method: 'PUT', body: file });
            setSitePlan({ id, recordId, url });
        });
    };

    const removeSitePlan = async () => {
        await removeSitePlanMutation({
            variables: { propertyId },
        });

        setSitePlan(null);
    };

    return (
        <div>
            <H4 className="mb-0.75rem">Site Plan</H4>
            <BodySmall className="mb-1.75rem">
                Accepted file formats: .doc, .docx, .pdf. Limit 1 file upload per property listing. Limit 10 MB file
                size.
            </BodySmall>

            {!sitePlan?.id && (
                <PhotoUpload
                    title="Site Plan"
                    fileTypes=".doc,.docx,.pdf"
                    maxSize={10000000}
                    maxFiles={1}
                    banner
                    onDrop={addSitePlan}
                />
            )}

            {sitePlan?.id && (
                <FilePreviewContainerOuter>
                    <FilePreviewContainerInner>
                        <div className="text-center">
                            <div>
                                <CircleCheckIconNucleo />
                            </div>

                            <div>
                                <LinkSmall
                                    href={sitePlan?.url}
                                    className="text-primary cursor-pointer"
                                    target="_blank"
                                >
                                    View
                                </LinkSmall>
                            </div>

                            <div>
                                <LinkSmall
                                    href="#"
                                    className="text-primary cursor-pointer"
                                    onClick={e => {
                                        e.preventDefault();
                                        removeSitePlan();
                                    }}
                                >
                                    Remove
                                </LinkSmall>
                            </div>
                        </div>
                    </FilePreviewContainerInner>

                    <FilePreviewBanner>
                        <LabelLarge>Site Plan</LabelLarge>
                    </FilePreviewBanner>
                </FilePreviewContainerOuter>
            )}
        </div>
    );
};

PropertySitePlan.propTypes = {
    propertyId: PropTypes.string.isRequired,
    sitePlan: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
    }),
};

export default PropertySitePlan;
